import { useState, useCallback, useRef, Fragment } from "react";
import { useParams } from "react-router-dom";
import ConfirmModal from "./ConfirmModal.jsx";
import HoverTextField, { EMPTY_COLOR } from "./HoverTextField.jsx";
import useMaaraaikaistarkastusData from "./hooks/useMaaraaikaistarkastusData.js";

// Self-contained, live-DB-backed component — same family as CapexYhteenveto.jsx
// (reads its own data via usePTS()-style context, no { data, onChange } props
// from a caller). Used identically in both places it appears:
//   1. The Taloyhtiökortti "Järjestelmäkuvaus" tab (pages/Taloyhtiokortti.js),
//      passed `editable` — the only place edits/version management happen.
//   2. A report block (blockUtils.js's registry) — `editable` omitted, so it
//      renders read-only. Previously this stored its own frozen snapshot in
//      the report's JSON content (report_sections.content), which could
//      drift from the live kiinteistö data; now both places always show the
//      exact same thing, since both read the same kiinteistoId-scoped
//      backend data — there is no more per-report copy of this table at all.
// Template-editing view (no kiinteistoId in the route) has no live data
// context to read, so this renders nothing there — same precedent as
// CapexYhteenveto.jsx's own `if (!footerTotals?.length) return null`.

const SOV_OPTIONS = [
  { value: "-", label: "–" },
  { value: "kylla", label: "Kyllä" },
  { value: "ei", label: "Ei" },
];

const STATUS_META = {
  puuttuu: { label: "Tieto puuttuu", color: "#6f42c1" },
  voimassa: { label: "Voimassa", color: "#04aa00" },
  vanhentunut: { label: "Vanhentunut", color: "#ba3b46" },
  ei_sovellu: { label: "Ei sovelleta", color: "#888" },
};

// HoverTextField itself (and its EMPTY_COLOR constant) now live in
// HoverTextField.jsx, shared with RakennuslupaTaulukko.jsx. HoverSelectField
// and HoverStatusField stay local — they're specific to this table's
// Sovelletaanko/Dokumentti/Tila enum fields.
function HoverSelectField({ value, onChange, options, readOnly = false }) {
  const [editing, setEditing] = useState(false);
  if (editing && !readOnly) {
    return (
      <select autoFocus className="form-select form-select-sm" value={value}
        onChange={e => { onChange(e.target.value); setEditing(false); }}
        onBlur={() => setEditing(false)}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  const isEmpty = value === "-" || !value;
  const label = isEmpty
    ? (readOnly ? "–" : (
        <span style={{ color: EMPTY_COLOR, fontWeight: 600 }}>
          <i className="bi bi-exclamation-circle me-1" style={{ fontSize: "0.85em" }} />Ei valittu
        </span>
      ))
    : (options.find(o => o.value === value)?.label ?? value);
  if (readOnly) return <div>{label}</div>;
  return (
    <div className="matt-hover-field" role="button" tabIndex={0} onClick={() => setEditing(true)}>
      {label}
    </div>
  );
}

function HoverStatusField({ value, onChange, readOnly = false }) {
  const [editing, setEditing] = useState(false);
  const meta = STATUS_META[value] || STATUS_META.puuttuu;
  if (editing && !readOnly) {
    return (
      <select autoFocus className="form-select form-select-sm fw-semibold" style={{ color: meta.color }}
        value={value} onChange={e => { onChange(e.target.value); setEditing(false); }}
        onBlur={() => setEditing(false)}>
        {Object.entries(STATUS_META).map(([key, m]) => (
          <option key={key} value={key} style={{ color: m.color }}>{m.label}</option>
        ))}
      </select>
    );
  }
  return (
    <span className="badge rounded-pill" role={readOnly ? undefined : "button"} tabIndex={readOnly ? undefined : 0}
      style={{ background: meta.color, cursor: readOnly ? "default" : "pointer" }}
      onClick={readOnly ? undefined : () => setEditing(true)}>
      {meta.label}
    </span>
  );
}

const newRow = (overrides = {}) => ({
  id: crypto.randomUUID(),
  nimi: "",
  saadosviite: "",
  vali: "",
  sovelletaanko: "-",
  viimeisin: "",
  seuraava: "",
  vastuutaho: "",
  tila: "puuttuu",
  dokumentti: "-",
  toimenpiteet: "",
  ...overrides,
});

const newGroup = (title = "") => ({ id: crypto.randomUUID(), title, rows: [newRow()] });

// Every editable field on a row — used by commit() below to diff old vs.
// new state and fire granular onRowFieldChange events.
const ROW_FIELDS = [
  "nimi", "saadosviite", "vali", "sovelletaanko", "viimeisin",
  "seuraava", "vastuutaho", "tila", "dokumentti", "toimenpiteet",
];

const SHARED_STYLE = `
  .matt-card-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 10px; margin-top: 4px; }
  .matt-card { border-radius: 10px; padding: 12px; background: #fafafa; }
  .matt-card-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 14px; margin-top: 8px; font-size: 0.78rem; }
  .matt-card-fields .matt-field-label { font-size: 0.62rem; color: #8e8e93; margin-bottom: 1px; }
  .matt-card input, .matt-card select { font-size: 0.78rem; }
  .matt-citation { font-size: 0.68rem; color: #8e8e93; }
  /* Hover-to-edit affordance — same idiom as EditableRow.jsx's Perustiedot
     fields: plain text at rest, a soft tint on hover signals "click to
     edit", no border/box chrome either way. */
  .matt-hover-field { cursor: pointer; border-radius: 6px; padding: 2px 4px; margin: -2px -4px; transition: background-color 0.12s ease, font-size 0.12s ease; }
  .matt-hover-field:hover { background-color: #eafbf1; font-size: 1.1em; }
  .matt-group-head th { background: #eafbf1; color: #198754; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; font-size: 0.72rem; }
  /* Soft pill, same green tint as the hover-field affordance above — reads
     as a calm "add" action, not a boxy outline button fighting for
     attention between the row list and the next group header. */
  .matt-add-row-btn { background: #eafbf1; color: #198754; border: none; border-radius: 999px; padding: 5px 16px; font-size: 0.78rem; font-weight: 600; transition: background-color 0.12s ease; }
  .matt-add-row-btn:hover { background: #cdf0db; color: #146c43; }
  .matt-table th { font-size: 0.7rem; color: #495057; white-space: nowrap; vertical-align: bottom; padding: 6px 10px; }
  .matt-table td { vertical-align: top; font-size: 0.78rem; padding: 6px 10px; }
  .mt-version-bar { display: flex; align-items: baseline; gap: 18px; flex-wrap: wrap; justify-content: center; margin-bottom: 10px; }
  .mt-version-field { display: flex; align-items: baseline; gap: 8px; }
  .mt-version-select {
    appearance: none; -webkit-appearance: none; background-color: transparent; border: none;
    font-family: inherit; font-size: 0.95rem; font-weight: 800; color: #198754; cursor: pointer;
    padding-right: 18px; margin: 0;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: right center; background-size: 14px;
  }
  .mt-version-select:focus-visible { outline: 2px solid #198754; outline-offset: 2px; border-radius: 4px; }
  .mt-version-tag { font-size: 0.68rem; font-weight: 700; padding: 2px 8px; border-radius: 999px; white-space: nowrap; }
  .mt-version-tag.current { color: #198754; background: #eafbf1; }
  .mt-version-tag.historical { color: #8e8e93; background: #f1f3f5; }
  .mt-version-actions { display: flex; gap: 16px; }
  .mt-version-link { background: none; border: none; font-family: inherit; font-size: 0.82rem; font-weight: 700; color: #8e8e93; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; padding: 0; }
  .mt-version-link:hover:not(:disabled) { color: #198754; }
  .mt-version-link.danger:hover:not(:disabled) { color: #dc3545; }
  .mt-version-link:disabled { opacity: 0.4; cursor: not-allowed; }
`;

// Identical in every usage — the Taloyhtiökortti tab and a report block
// both get the exact same full editing UI, version switcher included, with
// no mode distinction between them (there was one; it's gone, per explicit
// instruction — this is the same table everywhere, not a read-only report
// mirror of a separately-editable live one). The only thing that's ever
// read-only is browsing a non-current (historical) version, which applies
// identically regardless of where the component is rendered.
export default function MaaraaikaistarkastusTaulukko() {
  const { id: kiinteistoId } = useParams();
  const mt = useMaaraaikaistarkastusData(kiinteistoId);
  const [confirmDeleteVersioId, setConfirmDeleteVersioId] = useState(null);

  // Template-editing view (no kiinteistö in the route) has no live data to
  // show at all — same as CapexYhteenveto.jsx's own early-return.
  if (!kiinteistoId) return null;
  if (mt.loading) return <div className="text-center text-muted py-4">Ladataan…</div>;

  const effectiveReadOnly = !mt.isCurrent;

  return (
    <>
      <style>{SHARED_STYLE}</style>

      <div className="mt-version-bar">
        <div className="mt-version-field">
          <select
            className="mt-version-select"
            value={mt.activeVersioId || ""}
            onChange={e => mt.switchVersion(Number(e.target.value))}
          >
            {mt.versions.map(v => (
              <option key={v.id} value={v.id}>{v.label}</option>
            ))}
          </select>
          <span className={`mt-version-tag ${mt.isCurrent ? "current" : "historical"}`}>
            {mt.isCurrent ? "nykyinen" : "vanha versio"}
          </span>
        </div>
        <div className="mt-version-actions">
          <button type="button" className="mt-version-link" onClick={() => mt.createVersion()}>
            <i className="bi bi-plus-lg" /> Uusi versio
          </button>
          <button
            type="button"
            className="mt-version-link danger"
            disabled={mt.versions.length <= 1}
            title={mt.versions.length <= 1 ? "Viimeistä versiota ei voi poistaa" : "Poista tämä versio"}
            onClick={() => setConfirmDeleteVersioId(mt.activeVersioId)}
          >
            <i className="bi bi-trash" /> Poista
          </button>
        </div>
        {mt.saving && <span className="text-muted small">Tallennetaan…</span>}
        {!mt.saving && mt.savedFlash && <span className="small" style={{ color: "#198754" }}>Tallennettu <i className="bi bi-check-lg" /></span>}
        {mt.error && <span className="text-danger small">{mt.error}</span>}
      </div>

      {/* Keyed on activeVersioId so switching versions remounts this with a
          fresh local draft — it seeds its own state once from `groups` and
          never resyncs from changed props after that, same as before. */}
      <MaaraaikaistarkastusTaulukkoView
        key={mt.activeVersioId}
        groups={mt.data?.groups || []}
        readOnly={effectiveReadOnly}
        onGroupAdd={effectiveReadOnly ? undefined : mt.onGroupAdd}
        onGroupTitleChange={effectiveReadOnly ? undefined : mt.onGroupTitleChange}
        onGroupDelete={effectiveReadOnly ? undefined : mt.onGroupDelete}
        onRowAdd={effectiveReadOnly ? undefined : mt.onRowAdd}
        onRowFieldChange={effectiveReadOnly ? undefined : mt.onRowFieldChange}
        onRowDelete={effectiveReadOnly ? undefined : mt.onRowDelete}
      />

      <ConfirmModal
        show={confirmDeleteVersioId !== null}
        onClose={() => setConfirmDeleteVersioId(null)}
        onConfirm={() => { mt.deleteVersion(confirmDeleteVersioId); setConfirmDeleteVersioId(null); }}
        title="Poista versio"
        message="Haluatko varmasti poistaa tämän version pysyvästi? Kaikki sen ryhmät ja rivit poistuvat mukana."
        confirmText="Poista"
        cancelText="Peru"
      />
    </>
  );
}

function MaaraaikaistarkastusTaulukkoView({
  groups: initialGroups, readOnly,
  onGroupAdd, onGroupTitleChange, onGroupDelete,
  onRowAdd, onRowFieldChange, onRowDelete,
}) {
  const [groups, setGroups] = useState(initialGroups);
  const [confirmTarget, setConfirmTarget] = useState(null); // { type: 'row'|'group', groupId, rowId? }
  const [viewMode, setViewMode] = useState("table"); // 'cards' | 'table'

  // Local draft layered on top of the live data this mounted with — every
  // keystroke updates this cheaply (no API call), and commit() below is the
  // one place that actually calls the live mutation callbacks, diffed
  // against lastCommittedRef so only real changes trigger a write.
  const lastCommittedRef = useRef(groups);

  const commit = useCallback((nextGroups) => {
    const next = nextGroups ?? groups;
    const prev = lastCommittedRef.current;
    if (onGroupTitleChange || onRowFieldChange) {
      const prevGroupsById = new Map(prev.map(g => [g.id, g]));
      for (const g of next) {
        const prevG = prevGroupsById.get(g.id);
        if (!prevG) continue; // newly added group — onGroupAdd already covered it
        if (prevG.title !== g.title) onGroupTitleChange?.(g.id, g.title);
        const prevRowsById = new Map(prevG.rows.map(r => [r.id, r]));
        for (const r of g.rows) {
          const prevR = prevRowsById.get(r.id);
          if (!prevR) continue; // newly added row — onRowAdd already covered it
          for (const field of ROW_FIELDS) {
            if (prevR[field] !== r[field]) onRowFieldChange?.(g.id, r.id, field, r[field]);
          }
        }
      }
    }
    lastCommittedRef.current = next;
  }, [groups, onGroupTitleChange, onRowFieldChange]);

  const handleBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) commit(groups);
  };

  const updateRow = (groupId, rowId, field, value, immediate = false) => {
    const next = groups.map(g => g.id !== groupId ? g : {
      ...g,
      rows: g.rows.map(r => r.id === rowId ? { ...r, [field]: value } : r),
    });
    setGroups(next);
    if (immediate) commit(next);
  };

  const updateGroupTitle = (groupId, title) => {
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, title } : g));
  };

  const addRow = (groupId) => {
    const row = newRow();
    const next = groups.map(g => g.id === groupId ? { ...g, rows: [...g.rows, row] } : g);
    setGroups(next);
    lastCommittedRef.current = next;
    onRowAdd?.(groupId, row);
  };

  const addGroup = () => {
    const group = newGroup("Uusi ryhmä");
    const next = [...groups, group];
    setGroups(next);
    lastCommittedRef.current = next;
    onGroupAdd?.(group);
  };

  const confirmRemove = () => {
    if (!confirmTarget) return;
    let next;
    if (confirmTarget.type === "row") {
      next = groups.map(g => g.id !== confirmTarget.groupId ? g : { ...g, rows: g.rows.filter(r => r.id !== confirmTarget.rowId) });
      onRowDelete?.(confirmTarget.groupId, confirmTarget.rowId);
    } else {
      next = groups.filter(g => g.id !== confirmTarget.groupId);
      onGroupDelete?.(confirmTarget.groupId);
    }
    setGroups(next);
    lastCommittedRef.current = next;
    setConfirmTarget(null);
  };

  return (
    <>
      {/* Card grid at every breakpoint — CSS grid auto-fill naturally
          collapses to one column on narrow screens and tiles several
          columns on wide ones (≈3 on a typical desktop width), so the
          same markup serves both instead of maintaining a separate
          9-column table for desktop and a card stack for mobile. */}
      <div className="border rounded bg-white p-2 wide-table-block" onBlur={handleBlur}>
        <div className="d-flex justify-content-center mb-2">
          <div className="btn-group btn-group-sm" role="group">
            <button type="button" className={`btn ${viewMode === "table" ? "btn-success" : "btn-outline-success"}`}
              onClick={() => setViewMode("table")}>
              Taulukko
            </button>
            <button type="button" className={`btn ${viewMode === "cards" ? "btn-success" : "btn-outline-success"}`}
              onClick={() => setViewMode("cards")}>
              Kortit
            </button>
          </div>
        </div>

        {viewMode === "cards" && (
        <div>
          {groups.map(group => (
            <div key={group.id} className="mb-3">
              <div className="d-flex align-items-center justify-content-between mt-2 mb-1">
                {readOnly ? (
                  <div className="fw-bold flex-grow-1" style={{ color: "#198754", textTransform: "uppercase", letterSpacing: "0.04em", fontSize: "0.72rem" }}>
                    {group.title}
                  </div>
                ) : (
                  <input
                    className="form-control form-control-sm fw-bold border-0 bg-transparent p-0 flex-grow-1"
                    style={{ color: "#198754", textTransform: "uppercase", letterSpacing: "0.04em", fontSize: "0.72rem" }}
                    value={group.title}
                    onChange={e => updateGroupTitle(group.id, e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
                    placeholder="Ryhmän otsikko"
                  />
                )}
                {!readOnly && (
                  <button type="button" className="btn btn-outline-danger btn-sm ms-2" style={{ padding: "1px 6px", fontSize: "0.65rem", flexShrink: 0 }}
                    onClick={() => setConfirmTarget({ type: "group", groupId: group.id })}>
                    Poista ryhmä
                  </button>
                )}
              </div>

              <div className="matt-card-grid">
              {group.rows.map(row => {
                return (
                  <div key={row.id} className="matt-card">
                    {/* X sits in the header, a fixed spot regardless of how
                        tall the rest of the card grows — relying on the
                        grid stretching every card in a row to equal height
                        and pinning the button to that (variable) bottom
                        edge made its position inconsistent card to card. */}
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <HoverTextField value={row.nimi} bold readOnly={readOnly}
                          onChange={val => updateRow(group.id, row.id, "nimi", val)} placeholder="Tarkastuksen nimi" />
                      </div>
                      {!readOnly && (
                        <button type="button" className="btn btn-outline-danger btn-sm" style={{ padding: "1px 7px", flexShrink: 0 }}
                          onClick={() => setConfirmTarget({ type: "row", groupId: group.id, rowId: row.id })}
                          disabled={group.rows.length === 1}
                          aria-label="Poista rivi" title="Poista rivi">
                          ✕
                        </button>
                      )}
                    </div>
                    <div className="matt-citation">
                      <HoverTextField value={row.saadosviite} readOnly={readOnly}
                        onChange={val => updateRow(group.id, row.id, "saadosviite", val)} placeholder="Säädösviite" />
                    </div>

                    <div className="matt-card-fields">
                      <div>
                        <div className="matt-field-label">Väli (v)</div>
                        <HoverTextField lexical namespace={`matt-vali-${row.id}`} value={row.vali} readOnly={readOnly}
                          onChange={val => updateRow(group.id, row.id, "vali", val)} placeholder="esim. 10" />
                      </div>
                      <div>
                        <div className="matt-field-label">Kohteessa</div>
                        <HoverSelectField value={row.sovelletaanko} options={SOV_OPTIONS} readOnly={readOnly}
                          onChange={val => updateRow(group.id, row.id, "sovelletaanko", val, true)} />
                      </div>
                      <div>
                        <div className="matt-field-label">Viimeisin</div>
                        <HoverTextField value={row.viimeisin} readOnly={readOnly}
                          onChange={val => updateRow(group.id, row.id, "viimeisin", val)} placeholder="vuosi" />
                      </div>
                      <div>
                        <div className="matt-field-label">Seuraava</div>
                        <HoverTextField value={row.seuraava} readOnly={readOnly}
                          onChange={val => updateRow(group.id, row.id, "seuraava", val)} placeholder="vuosi" />
                      </div>
                      <div>
                        <div className="matt-field-label">Vastuutaho</div>
                        <HoverTextField value={row.vastuutaho} readOnly={readOnly}
                          onChange={val => updateRow(group.id, row.id, "vastuutaho", val)} placeholder="Vastuutaho" />
                      </div>
                      <div>
                        <div className="matt-field-label">Dokumentti</div>
                        <HoverSelectField value={row.dokumentti} options={SOV_OPTIONS} readOnly={readOnly}
                          onChange={val => updateRow(group.id, row.id, "dokumentti", val, true)} />
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div className="matt-field-label">Tila</div>
                        <HoverStatusField value={row.tila} readOnly={readOnly}
                          onChange={val => updateRow(group.id, row.id, "tila", val, true)} />
                        {row.tila === "voimassa" && row.seuraava && (
                          <div className="small text-muted mt-1">seuraava {row.seuraava}</div>
                        )}
                      </div>
                    </div>

                    <div className="matt-field-label mt-2">Toimenpiteet / suositukset</div>
                    <HoverTextField lexical namespace={`matt-toimenpiteet-${row.id}`} value={row.toimenpiteet} readOnly={readOnly}
                      onChange={val => updateRow(group.id, row.id, "toimenpiteet", val)} placeholder="Toimenpidesuositus" />
                  </div>
                );
              })}
              </div>

              {!readOnly && (
                <div className="text-center my-3">
                  <button type="button" className="btn btn-sm matt-add-row-btn" onClick={() => addRow(group.id)}>
                    + Lisää rivi
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        )}

        {viewMode === "table" && (
        <div className="table-responsive">
          <table className="table table-sm matt-table mb-2" style={{ minWidth: 1100 }}>
            <thead>
              <tr>
                <th style={{ minWidth: 220 }}>Tarkastus</th>
                <th style={{ minWidth: 110 }}>Väli (v)</th>
                <th style={{ minWidth: 90 }}>Kohteessa</th>
                <th style={{ minWidth: 80 }}>Viimeisin</th>
                <th style={{ minWidth: 80 }}>Seuraava</th>
                <th style={{ minWidth: 120 }}>Vastuutaho</th>
                <th style={{ minWidth: 130 }}>Tila</th>
                <th style={{ minWidth: 90 }}>Dokumentti</th>
                <th style={{ minWidth: 180 }}>Toimenpiteet / suositukset</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {groups.map(group => (
                <Fragment key={group.id}>
                  <tr className="matt-group-head">
                    <th colSpan={9} className="py-1">
                      {readOnly ? (
                        <div className="fw-bold" style={{ color: "#198754", textTransform: "uppercase", letterSpacing: "0.04em", fontSize: "0.72rem" }}>
                          {group.title}
                        </div>
                      ) : (
                        <input
                          className="form-control form-control-sm fw-bold border-0 bg-transparent p-0"
                          style={{ color: "#198754", textTransform: "uppercase", letterSpacing: "0.04em", fontSize: "0.72rem" }}
                          value={group.title}
                          onChange={e => updateGroupTitle(group.id, e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
                          placeholder="Ryhmän otsikko"
                        />
                      )}
                    </th>
                    <th className="py-1 text-end">
                      {!readOnly && (
                        <button type="button" className="btn btn-outline-danger btn-sm" style={{ padding: "1px 6px", fontSize: "0.68rem" }}
                          onClick={() => setConfirmTarget({ type: "group", groupId: group.id })}>
                          Poista ryhmä
                        </button>
                      )}
                    </th>
                  </tr>

                  {group.rows.map(row => (
                    <tr key={row.id}>
                      <td>
                        <HoverTextField value={row.nimi} bold readOnly={readOnly}
                          onChange={val => updateRow(group.id, row.id, "nimi", val)} placeholder="Tarkastuksen nimi" />
                        <div className="matt-citation">
                          <HoverTextField value={row.saadosviite} readOnly={readOnly}
                            onChange={val => updateRow(group.id, row.id, "saadosviite", val)} placeholder="Säädösviite" />
                        </div>
                      </td>
                      <td>
                        <HoverTextField lexical namespace={`matt-vali-tbl-${row.id}`} value={row.vali} readOnly={readOnly}
                          onChange={val => updateRow(group.id, row.id, "vali", val)} placeholder="esim. 10" />
                      </td>
                      <td>
                        <HoverSelectField value={row.sovelletaanko} options={SOV_OPTIONS} readOnly={readOnly}
                          onChange={val => updateRow(group.id, row.id, "sovelletaanko", val, true)} />
                      </td>
                      <td>
                        <HoverTextField value={row.viimeisin} readOnly={readOnly}
                          onChange={val => updateRow(group.id, row.id, "viimeisin", val)} placeholder="vuosi" />
                      </td>
                      <td>
                        <HoverTextField value={row.seuraava} readOnly={readOnly}
                          onChange={val => updateRow(group.id, row.id, "seuraava", val)} placeholder="vuosi" />
                      </td>
                      <td>
                        <HoverTextField value={row.vastuutaho} readOnly={readOnly}
                          onChange={val => updateRow(group.id, row.id, "vastuutaho", val)} placeholder="Vastuutaho" />
                      </td>
                      <td>
                        <HoverStatusField value={row.tila} readOnly={readOnly}
                          onChange={val => updateRow(group.id, row.id, "tila", val, true)} />
                        {row.tila === "voimassa" && row.seuraava && (
                          <div className="small text-muted mt-1">seuraava {row.seuraava}</div>
                        )}
                      </td>
                      <td>
                        <HoverSelectField value={row.dokumentti} options={SOV_OPTIONS} readOnly={readOnly}
                          onChange={val => updateRow(group.id, row.id, "dokumentti", val, true)} />
                      </td>
                      <td>
                        <HoverTextField lexical namespace={`matt-toimenpiteet-tbl-${row.id}`} value={row.toimenpiteet} readOnly={readOnly}
                          onChange={val => updateRow(group.id, row.id, "toimenpiteet", val)} placeholder="Toimenpidesuositus" />
                      </td>
                      <td className="align-middle">
                        {!readOnly && (
                          <button type="button" className="btn btn-outline-danger btn-sm" style={{ padding: "2px 7px" }}
                            onClick={() => setConfirmTarget({ type: "row", groupId: group.id, rowId: row.id })}
                            disabled={group.rows.length === 1}>
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!readOnly && (
                    <tr>
                      <td colSpan={10} className="text-center py-3">
                        <button type="button" className="btn btn-sm matt-add-row-btn" onClick={() => addRow(group.id)}>
                          + Lisää rivi
                        </button>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        )}

        {!readOnly && (
          <div className="d-flex justify-content-center">
            <button type="button" className="btn btn-outline-success btn-sm" onClick={addGroup}>
              + Lisää ryhmä
            </button>
          </div>
        )}
      </div>

      <ConfirmModal
        show={confirmTarget !== null}
        onClose={() => setConfirmTarget(null)}
        onConfirm={confirmRemove}
        title={confirmTarget?.type === "group" ? "Poista ryhmä" : "Poista rivi"}
        message={confirmTarget?.type === "group"
          ? "Haluatko varmasti poistaa tämän ryhmän ja kaikki sen rivit?"
          : "Haluatko varmasti poistaa tämän rivin?"}
        confirmText="Poista"
        cancelText="Peru"
      />
    </>
  );
}
